import { apiClient, getData, getPage } from '@/api/client'
import type { components } from '@/api/schema'
import {
  basketBacktestSchema,
  basketDetailSchema,
  basketSchema,
  basketsMetaTagsSchema,
  basketStaySchema,
  inOutMetaOrNullSchema,
  verdictSchema,
  type Audience,
  type BasketDetail,
  type BasketList,
  type BasketBacktest,
  type BasketStay,
  type BasketInOut,
  type Verdict,
} from '@/schemas/baskets'
import { lenientList } from '@/schemas/common'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** One list's question. `tag: undefined` is any tag; an unknown slug answers an empty list. */
export interface BasketFilters {
  audience: Audience
  tag: string | undefined
}

/** mm-be's cap on `/history/`'s `limit`: the newest 200 stays. */
export const BASKET_HISTORY_LIMIT = 200

/** Home's "Baskets to try": the first four, in staff's display order (owner Q1). */
export const FEATURED_BASKETS = 4

/** mm-be's 404 for a slug no published basket has (absent, switched off, or with no columns). */
export const BASKET_NOT_FOUND = 'BASKET_NOT_FOUND'

/** mm-be's 404 for a symbol that is not an active listed stock (the one-company check). */
export const BASKET_UNKNOWN_STOCK = 'BASKET_UNKNOWN_STOCK'

/** The baskets seam: five login-only reads. Nothing here writes. */
export const basketsService = {
  /**
   * One audience's published baskets, in display order, narrowed to a tag when one is given, with
   * every tag the audience's baskets carry for the chips. Unpaged: mm-be lists them all.
   */
  list: async ({ audience, tag }: BasketFilters): Promise<BasketList> => {
    const { rows, meta } = await getPage<Wire<'BasketSummary'>>(() =>
      apiClient.GET('/api/v5/baskets/', {
        params: { query: { audience, ...(tag === undefined ? {} : { tag }) } },
      }),
    )
    return {
      baskets: lenientList(basketSchema, 'basket').parse(rows),
      tags: basketsMetaTagsSchema.parse(meta),
    }
  },

  /** One basket: its columns, its members' figures and how it is built. 404 `BASKET_NOT_FOUND`. */
  detail: async (slug: string): Promise<BasketDetail> =>
    basketDetailSchema.parse(
      await getData<Wire<'BasketDetail'>>(() =>
        apiClient.GET('/api/v5/baskets/{slug}/', { params: { path: { slug } } }),
      ),
    ),

  /**
   * Every stay of every company in the basket, newest first, up to the newest 200: a trader
   * basket's members come and go after every close.
   */
  history: async (slug: string): Promise<BasketStay[]> => {
    const { rows } = await getPage<Wire<'Stay'>>(() =>
      apiClient.GET('/api/v5/baskets/{slug}/history/', {
        params: { path: { slug }, query: { limit: BASKET_HISTORY_LIMIT } },
      }),
    )
    return lenientList(basketStaySchema, 'basket stay').parse(rows)
  },

  /**
   * Past results: Rs 1 lakh in the basket against the same in NEPSE, month by month (mm-be #375).
   * 404 for an unknown basket, and for every basket until that endpoint is deployed.
   */
  backtest: async (slug: string): Promise<BasketBacktest> =>
    basketBacktestSchema.parse(
      await getData<Wire<'Backtest'>>(() =>
        apiClient.GET('/api/v5/baskets/{slug}/backtest/', { params: { path: { slug } } }),
      ),
    ),

  /**
   * Every company in the basket's group against its checks (up to ~300, mm-be README "Bounds"),
   * with the funnel counts read off `meta`.
   */
  inOut: async (slug: string): Promise<BasketInOut> => {
    const { rows, meta } = await getPage<Wire<'Verdict'>>(() =>
      apiClient.GET('/api/v5/baskets/{slug}/in-out/', { params: { path: { slug } } }),
    )
    return {
      verdicts: lenientList(verdictSchema, 'basket verdict').parse(rows),
      funnel: inOutMetaOrNullSchema.parse(meta),
    }
  },

  /**
   * One company against the basket, for a symbol the In or Out list does not hold: it answers
   * `not_checked` for a listed stock outside the group. 404 `BASKET_UNKNOWN_STOCK` for a symbol
   * that is not listed.
   */
  check: async (slug: string, symbol: string): Promise<Verdict> =>
    verdictSchema.parse(
      await getData<Wire<'Verdict'>>(() =>
        apiClient.GET('/api/v5/baskets/{slug}/in-out/{symbol}/', {
          params: { path: { slug, symbol } },
        }),
      ),
    ),
}
